import { ChatSidebar } from '@/components/ai/ChatSidebar';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { BulkActionBar } from '@/components/programming/BulkActionBar';
import { CalendarDateFilter } from '@/components/programming/CalendarDateFilter';
import { CalendarView } from '@/components/programming/CalendarView';
import { FilterBar, emptyFilterValues } from '@/components/programming/FilterBar';
import type { FilterValues } from '@/components/programming/FilterBar';
import { TVDisplayButton } from '@/components/programming/TVDisplayButton';
import { WorkoutCreatorMetadata } from '@/components/programming/WorkoutCreatorMetadata';
import { WorkoutDetail } from '@/components/programming/WorkoutDetail';
import {
  WorkoutForm,
  type WorkoutFormData,
  normalizeWorkoutFormData,
} from '@/components/programming/WorkoutForm';
import { getCalendarDate } from '@/components/programming/calendarNavigation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminApi } from '@/hooks/useAdminApi';
import { useSelection } from '@/hooks/useSelection';
import { useSubmissionLock } from '@/hooks/useSubmissionLock';
import { type FieldErrors, firstInvalidField } from '@/lib/validationErrors';
import { type WorkoutAttribution, getProgrammingScoringLabel } from '@ergshoot/shared';
import { Calendar, Loader2, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CsvBulkUploadDialog } from './programming-workbench/CsvBulkUploadDialog';
import {
  type CsvUploadResult,
  downloadCsvTemplate as triggerCsvTemplateDownload,
} from './programming-workbench/model';
import { useCsvImport } from './programming-workbench/useCsvImport';

// Workout type matching API response
interface Workout {
  id: string;
  name: string;
  description: string | null;
  movements: string[];
  scoring_type: string;
  scoring_direction: string;
  equipment: string[];
  scheduled_date: string;
  track: { id: string; name: string | null } | null;
  category?: string | null;
  workout_type?: string | null;
  coach_notes?: string | null;
  video_url?: string | null;
  duration_minutes?: number | null;
  instructions?: string | null;
  attribution?: WorkoutAttribution | null;
  created_at: string;
  created_by_username?: string | null;
}

// Track type matching API response
interface Track {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  workout_count: number;
  created_at: string;
}

interface WorkoutSubmissionPayload {
  name: string;
  description: string;
  movements?: string[];
  scoring_type: string;
  scoring_direction: string;
  equipment?: string[];
  scheduled_date: string;
  track_id?: string;
  category?: string;
  workout_type: string;
}

interface DailyCsvUploadResult extends CsvUploadResult {
  created_workouts: Array<{ row: number; name: string; id: string }>;
}

export default function AdminProgramming() {
  const adminApi = useAdminApi();

  // Data state
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>();

  // Shared create/edit form state
  const [showWorkoutForm, setShowWorkoutForm] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState<Partial<WorkoutFormData>>();
  const [isSaving, setIsSaving] = useState(false);
  const uploadSubmission = useSubmissionLock<WorkoutSubmissionPayload>();
  const [editingWorkout, setEditingWorkout] = useState<Workout | null>(null);
  const updateSubmission = useSubmissionLock<WorkoutSubmissionPayload>();

  // AI Chat sidebar state
  const [showAiChat, setShowAiChat] = useState(false);

  // Bulk upload state (HQ-004)
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadTrackId, setBulkUploadTrackId] = useState('');
  const csvImport = useCsvImport<DailyCsvUploadResult>();

  // Selection state for bulk delete
  const selection = useSelection();
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Filter state (E-3)
  const [filters, setFilters] = useState<FilterValues>(emptyFilterValues);

  // View mode: table or calendar (F-2)
  const [viewMode, setViewMode] = useState<'table' | 'calendar'>('table');
  const [calendarDateFilter, setCalendarDateFilter] = useState<string | null>(null);

  // Source filter: 'all' (HQ workouts) or 'custom' (user-created)
  const [sourceFilter, setSourceFilter] = useState<'all' | 'custom'>('all');

  // Expanded row (F-4)
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Fetch workouts and tracks on mount
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [workoutsRes, tracksRes] = await Promise.all([
        adminApi.getProgramming({
          limit: 200,
          search: filters.search || undefined,
          category: filters.category || undefined,
          scoring_type: filters.scoring_type || undefined,
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
          equipment: filters.equipment || undefined,
          workout_type: sourceFilter === 'custom' ? undefined : 'daily_wod',
          source: sourceFilter === 'custom' ? 'custom' : undefined,
        }),
        adminApi.getTracks(),
      ]);

      if (workoutsRes.success && workoutsRes.data) {
        setWorkouts(workoutsRes.data.workouts);
      }

      if (tracksRes.success && tracksRes.data) {
        setTracks(tracksRes.data.tracks);
      }
    } catch (err) {
      console.error('Failed to fetch programming data:', err);
      setError('Failed to load programming data');
      toast.error('Failed to load programming data');
    } finally {
      setIsLoading(false);
    }
  }, [adminApi, filters, sourceFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const closeWorkoutForm = () => {
    uploadSubmission.reset();
    updateSubmission.reset();
    setShowWorkoutForm(false);
    setEditingWorkout(null);
    setFormInitialValues(undefined);
    setFieldErrors(undefined);
  };

  const openCreateForm = (initialValues?: Partial<WorkoutFormData>) => {
    uploadSubmission.reset();
    updateSubmission.reset();
    setEditingWorkout(null);
    setFormInitialValues(initialValues);
    setFieldErrors(undefined);
    setShowWorkoutForm(true);
  };

  const openEditForm = (workout: Workout) => {
    uploadSubmission.reset();
    updateSubmission.reset();
    setEditingWorkout(workout);
    setFieldErrors(undefined);
    setFormInitialValues({
      name: workout.name,
      description: workout.description || '',
      movements: workout.movements?.join('\n') || '',
      track_id: workout.track?.id || '',
      scheduled_date: workout.scheduled_date,
      scoring_type: workout.scoring_type,
      scoring_direction: workout.scoring_direction || 'descending',
      equipment: workout.equipment?.join(', ') || '',
      category: workout.category || '',
    });
    setShowWorkoutForm(true);
  };

  const handleSaveWorkout = async (formData: WorkoutFormData) => {
    setFieldErrors(undefined);

    if (!formData.name || !formData.description || !formData.scheduled_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    const normalized = normalizeWorkoutFormData(formData);
    const payload: WorkoutSubmissionPayload = {
      name: normalized.name,
      description: normalized.description,
      movements: normalized.movements.length > 0 ? normalized.movements : undefined,
      scoring_type: normalized.scoring_type,
      scoring_direction: normalized.scoring_direction,
      equipment: normalized.equipment.length > 0 ? normalized.equipment : undefined,
      scheduled_date: normalized.scheduled_date,
      track_id: normalized.track_id,
      category: normalized.category,
      workout_type: 'daily_wod',
    };
    const submission = editingWorkout ? updateSubmission : uploadSubmission;
    const attempt = submission.begin(payload);
    if (!attempt) return;

    setIsSaving(true);
    let succeeded = false;

    try {
      const response = editingWorkout
        ? await adminApi.updateWorkout(editingWorkout.id, attempt.payload, attempt.idempotencyKey)
        : await adminApi.createWorkout(attempt.payload, attempt.idempotencyKey);

      if (response.success) {
        succeeded = true;
        toast.success(
          editingWorkout ? 'Workout updated successfully!' : 'Workout uploaded successfully!'
        );
        closeWorkoutForm();
        fetchData();
      } else {
        if (
          showProgrammingFieldErrors(
            response.details,
            setFieldErrors,
            editingWorkout ? 'edit-' : undefined
          )
        ) {
          return;
        }
        toast.error(
          response.error ||
            (editingWorkout ? 'Failed to update workout' : 'Failed to upload workout')
        );
      }
    } catch (err) {
      console.error(editingWorkout ? 'Update error:' : 'Upload error:', err);
      toast.error(editingWorkout ? 'Failed to update workout' : 'Failed to upload workout');
    } finally {
      if (!succeeded) submission.release();
      setIsSaving(false);
    }
  };

  // Handle AI-generated workout
  const handleAiWorkoutGenerated = (workout: {
    name: string;
    description?: string;
    movements?: string[];
    scoring_type: string;
    scoring_direction?: string;
    equipment?: string[];
    scheduled_date?: string; // Optional - user will set
    track?: string; // Optional track name
  }) => {
    // Pre-fill the upload form with AI-generated data
    // Build description: prefer AI description, fallback to movements list
    let description = workout.description || '';
    if (!description && workout.movements?.length) {
      // Fallback: build description from movements
      description = workout.movements.map((m) => `• ${m}`).join('\n');
    }
    // Add equipment info if not already in description
    if (workout.equipment?.length && !description.includes('Equipment')) {
      description += `\n\n**Equipment:** ${workout.equipment.join(', ')}`;
    }
    // Map scoring type (AI uses these values, form expects same)
    const validScoringTypes = ['time', 'rounds_reps', 'reps', 'distance', 'calories'];
    const scoringType = validScoringTypes.includes(workout.scoring_type)
      ? workout.scoring_type
      : 'time'; // default fallback
    // Set track - default to RX if not specified
    // Look for matching track by name or use track-rx as default
    const trackMatch = tracks.find(
      (t) => t.name.toLowerCase() === (workout.track || 'rx').toLowerCase()
    );
    openCreateForm({
      name: workout.name,
      description,
      movements: workout.movements?.join('\n') || '',
      equipment: workout.equipment?.join(', ') || '',
      scheduled_date: workout.scheduled_date || '',
      scoring_type: scoringType,
      scoring_direction: workout.scoring_direction || 'descending',
      track_id: trackMatch?.id || 'track-rx',
    });
    setShowAiChat(false);
    toast.success('Workout ready! Please select a date and save.');
  };

  const selectedBulkUploadTrack = tracks.find((track) => track.id === bulkUploadTrackId);

  const handleBulkUpload = () =>
    csvImport.runUpload({
      upload: (csvContent) => adminApi.bulkUploadWorkouts(csvContent, bulkUploadTrackId),
      onSuccess: (result) => {
        if (result.created > 0) {
          toast.success(`${result.created} workout(s) created!`);
          fetchData();
        }
        if (result.errors > 0) {
          toast.error(`${result.errors} row(s) had errors`);
        }
      },
      failureMessage: 'Bulk upload failed',
      unexpectedMessage: 'Failed to process bulk upload',
    });

  // Bulk delete handler
  const handleBulkDelete = async () => {
    const ids = Array.from(selection.selectedIds);
    if (ids.length === 0) return;

    if (
      !confirm(`Are you sure you want to delete ${ids.length} workout(s)? This cannot be undone.`)
    ) {
      return;
    }

    setIsBulkDeleting(true);
    try {
      // First attempt without force
      const response = await adminApi.bulkDeleteWorkouts(ids);
      if (response.success && response.data) {
        const { deleted, skipped, details } = response.data;
        if (deleted > 0) {
          toast.success(`Deleted ${deleted} workout(s)`);
        }
        // If some were skipped due to scores, offer force-delete
        if (skipped > 0 && details.skipped.some((s) => s.reason.includes('score'))) {
          const scoreSkipped = details.skipped.filter((s) => s.reason.includes('score'));
          const forceConfirm = confirm(
            `${scoreSkipped.length} workout(s) have logged scores and were skipped.\n\nDo you want to delete them anyway? This will also remove all associated scores.`
          );
          if (forceConfirm) {
            const forceIds = scoreSkipped.map((s) => s.id);
            const forceResponse = await adminApi.bulkDeleteWorkouts(forceIds, true);
            if (forceResponse.success && forceResponse.data) {
              toast.success(
                `Force-deleted ${forceResponse.data.deleted} workout(s) and their scores`
              );
            }
          }
        } else if (skipped > 0) {
          const reasons = details.skipped.map((s) => s.reason).join('; ');
          toast.warning(`${skipped} skipped: ${reasons}`);
        }
        selection.deselectAll();
        fetchData();
      } else {
        toast.error(response.error || 'Bulk delete failed');
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
      toast.error('Failed to delete workouts');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Single workout delete handler
  const handleSingleDelete = async (workout: { id: string; name: string }) => {
    if (!confirm(`Delete "${workout.name}"?`)) return;

    try {
      const response = await adminApi.deleteWorkout(workout.id);
      if (response.success) {
        toast.success('Workout deleted');
        fetchData();
        return;
      }
      // If blocked by scores, offer force-delete
      if (response.error?.includes('scores')) {
        const forceConfirm = confirm(
          'This workout has logged scores. Delete anyway?\nThis will also remove all associated scores.'
        );
        if (forceConfirm) {
          const forceResponse = await adminApi.deleteWorkout(workout.id, true);
          if (forceResponse.success) {
            toast.success('Workout and scores deleted');
            fetchData();
          } else {
            toast.error(forceResponse.error || 'Force delete failed');
          }
        }
      } else {
        toast.error(response.error || 'Delete failed');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete workout');
    }
  };

  // Row 1 is a deletable example showing correct formats:
  // - duration: number only (e.g. 30, not "30m" or "30 mins")
  // - workout/equipment: pipe-separated (e.g. "RowErg|Wall Balls")
  // - metrics: time | reps | rounds_reps | distance | calories | shots
  // - date: YYYY-MM-DD
  const CSV_TEMPLATE =
    'Categories,date,title,description,duration,workout,equipment,Coach notes,video_url,metrics\n"Single Erg","2026-03-01","5 x 500m Row on 3min rest between efforts","Short description of the workout","30","500m Row|Pull-ups","RowErg|Pull-up Bar","Coaching cue or pacing note","","time"';

  const downloadCsvTemplate = () => {
    triggerCsvTemplateDownload(CSV_TEMPLATE, 'workout_upload_template.csv');
  };

  // WOD style options (renamed per UAT Feb 2026 — Matt's requested labels)
  const wodStyles = [
    { value: '', label: 'Select style...' },
    { value: 'single_erg', label: 'Single Erg' },
    { value: 'dual_erg', label: 'Dual Erg' },
    { value: 'medley', label: 'Medley (three ergs)' },
    { value: 'hybrid', label: 'Hybrid (functional & shooting)' },
    { value: 'team_relay', label: 'Team Relay' },
    { value: 'endurance', label: 'Endurance' },
    { value: 'standard', label: 'Standard' },
  ];

  // Sort all workouts by date ascending (chronological: first → last day of month)
  const sortedWorkouts = [...workouts].sort(
    (a, b) => new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime()
  );
  const allIds = sortedWorkouts.map((w) => w.id);

  const handleCalendarMonthChange = useCallback((y: number, m: number) => {
    const firstDay = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    const lastDay = `${y}-${String(m + 1).padStart(2, '0')}-${new Date(y, m + 1, 0).getDate()}`;
    setCalendarDateFilter(null);
    setFilters((prev) => ({ ...prev, date_from: firstDay, date_to: lastDay }));
  }, []);

  const handleCalendarDateClick = useCallback((date: string) => {
    setCalendarDateFilter(date);
    setViewMode('table');
    setFilters((prev) => ({ ...prev, date_from: date, date_to: date }));
  }, []);

  const handleFiltersChange = useCallback((nextFilters: FilterValues) => {
    setCalendarDateFilter((currentDate) =>
      currentDate && nextFilters.date_from === currentDate && nextFilters.date_to === currentDate
        ? currentDate
        : null
    );
    setFilters(nextFilters);
  }, []);

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Daily WODs</h1>
          <p className="mt-1 text-muted-foreground">Upload and manage daily workout programming.</p>
        </div>
        <div className="flex gap-2">
          {/* AI Program Create hidden until feature is ready
          <Button
            onClick={() => setShowAiChat(true)}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            AI Program Create
          </Button>
          */}
          <Button
            variant="outline"
            onClick={() => {
              setShowBulkUploadModal(true);
              setBulkUploadTrackId('');
              csvImport.reset();
            }}
          >
            <Upload className="h-4 w-4 mr-2" />
            Bulk Upload
          </Button>
          <Button className="gap-2" data-testid="add-wod-btn" onClick={() => openCreateForm()}>
            <Plus className="h-4 w-4" />
            Add WOD
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Card className="mb-8 border-destructive">
          <CardContent className="p-6">
            <p className="text-destructive">{error}</p>
            <Button variant="outline" onClick={fetchData} className="mt-4">
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters (E-3) */}
      <FilterBar
        config={{
          showSearch: true,
          showCategory: true,
          showScoring: true,
          showDate: true,
          showEquipment: true,
          categoryOptions: wodStyles.filter((s) => s.value !== ''),
        }}
        values={filters}
        onChange={handleFiltersChange}
        resultCount={workouts.length}
      />

      {/* Workouts Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              {sourceFilter === 'custom' ? 'Custom Workouts' : 'All Workouts'}
              <Badge variant="secondary" className="ml-2">
                {sortedWorkouts.length} workouts
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setSourceFilter('all')}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    sourceFilter === 'all'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card hover:bg-accent text-muted-foreground'
                  }`}
                >
                  HQ Workouts
                </button>
                <button
                  type="button"
                  onClick={() => setSourceFilter('custom')}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    sourceFilter === 'custom'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card hover:bg-accent text-muted-foreground'
                  }`}
                >
                  Custom
                </button>
              </div>
            </div>
            <div className="flex rounded-lg border border-border overflow-hidden">
              <button
                type="button"
                onClick={() => {
                  setViewMode('table');
                  setCalendarDateFilter(null);
                  setFilters((prev) => ({ ...prev, date_from: '', date_to: '' }));
                }}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === 'table'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card hover:bg-accent text-muted-foreground'
                }`}
              >
                Table
              </button>
              <button
                type="button"
                onClick={() => {
                  setViewMode('calendar');
                  setCalendarDateFilter(null);
                  const now = new Date();
                  const y = now.getFullYear();
                  const m = now.getMonth();
                  const firstDay = `${y}-${String(m + 1).padStart(2, '0')}-01`;
                  const lastDay = `${y}-${String(m + 1).padStart(2, '0')}-${new Date(y, m + 1, 0).getDate()}`;
                  setFilters((prev) => ({ ...prev, date_from: firstDay, date_to: lastDay }));
                }}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card hover:bg-accent text-muted-foreground'
                }`}
              >
                Calendar
              </button>
            </div>
          </div>
          <CardDescription>All daily WOD programming sorted by date</CardDescription>
        </CardHeader>
        <CardContent>
          {calendarDateFilter && (
            <div className="mb-4">
              <CalendarDateFilter
                date={calendarDateFilter}
                onClear={() => {
                  setCalendarDateFilter(null);
                  setFilters((prev) => ({ ...prev, date_from: '', date_to: '' }));
                }}
              />
            </div>
          )}
          {viewMode === 'calendar' ? (
            <CalendarView
              workouts={sortedWorkouts}
              loading={isLoading}
              onDayClick={handleCalendarDateClick}
              onWorkoutClick={(w) => handleCalendarDateClick(getCalendarDate(w.scheduled_date))}
              onMonthChange={handleCalendarMonthChange}
            />
          ) : isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : sortedWorkouts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-1">No workouts found</h3>
              <p className="text-muted-foreground">Add your first workout to get started.</p>
              <Button variant="outline" className="mt-4" onClick={() => openCreateForm()}>
                <Plus className="h-4 w-4 mr-2" />
                Create Workout
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selection.isAllSelected(allIds)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            selection.selectAll(allIds);
                          } else {
                            selection.deselectAll();
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Workout</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Scoring</TableHead>
                    <TableHead>Equipment</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedWorkouts.map((workout) => {
                    const date = new Date(workout.scheduled_date);
                    const isExpanded = expandedId === workout.id;
                    return (
                      <React.Fragment key={workout.id}>
                        <TableRow
                          className="animate-fade-in cursor-pointer hover:bg-accent/50"
                          onClick={() => setExpandedId(isExpanded ? null : workout.id)}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selection.isSelected(workout.id)}
                              onCheckedChange={() => selection.toggle(workout.id)}
                            />
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {date.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{workout.name}</p>
                              {!isExpanded && workout.description && (
                                <p className="text-sm text-muted-foreground line-clamp-1">
                                  {workout.description}
                                </p>
                              )}
                              <WorkoutCreatorMetadata
                                attribution={workout.attribution}
                                created_at={workout.created_at}
                                created_by_username={workout.created_by_username}
                                className="mt-1"
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            {workout.category ? (
                              <Badge variant="secondary" className="text-xs">
                                {wodStyles.find((s) => s.value === workout.category)?.label ||
                                  workout.category}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {getProgrammingScoringLabel(workout.scoring_type)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {workout.equipment && workout.equipment.length > 0 ? (
                              <span className="text-sm">
                                {workout.equipment.slice(0, 2).join(', ')}
                                {workout.equipment.length > 2 &&
                                  ` +${workout.equipment.length - 2}`}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <TVDisplayButton workoutId={workout.id} />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditForm(workout)}
                              >
                                <Pencil className="h-3 w-3 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                                onClick={() => handleSingleDelete(workout)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow className="cursor-pointer" onClick={() => setExpandedId(null)}>
                            <TableCell colSpan={7} className="p-0">
                              <WorkoutDetail
                                workout={workout}
                                onClose={() => setExpandedId(null)}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        <BulkActionBar
          count={selection.count}
          onDelete={handleBulkDelete}
          onCancel={selection.deselectAll}
          isDeleting={isBulkDeleting}
        />
      </Card>

      {showWorkoutForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="w-full max-w-lg mx-4 animate-fade-in max-h-[90vh] overflow-y-auto">
            <WorkoutForm
              variant="daily"
              title={editingWorkout ? 'Edit Workout' : 'Upload Workout'}
              subtitle={
                editingWorkout
                  ? 'Update the workout details'
                  : 'Add a new workout to the programming'
              }
              submitLabel={editingWorkout ? 'Save Changes' : 'Upload Workout'}
              headerIcon={
                editingWorkout ? (
                  <Pencil className="h-5 w-5 text-primary" />
                ) : (
                  <Upload className="h-5 w-5 text-primary" />
                )
              }
              initialValues={formInitialValues}
              defaultScoringDirection="descending"
              isSubmitting={isSaving || uploadSubmission.isLocked || updateSubmission.isLocked}
              minimumScheduledDate={
                editingWorkout ? undefined : new Date().toISOString().split('T')[0]
              }
              fieldIds={{
                name: editingWorkout ? 'edit-workout-name' : 'workout-name',
                scheduledDate: editingWorkout ? 'edit-scheduled-date' : 'scheduled-date',
                description: editingWorkout ? 'edit-workout-description' : 'workout-description',
                movements: editingWorkout ? 'edit-workout-movements' : 'workout-movements',
                equipment: editingWorkout ? 'edit-workout-equipment' : 'workout-equipment',
              }}
              fieldErrors={fieldErrors}
              onSubmit={handleSaveWorkout}
              onCancel={closeWorkoutForm}
            />
          </div>
        </div>
      )}
      {showBulkUploadModal && (
        <CsvBulkUploadDialog
          title="Bulk Upload Workouts"
          description="Upload multiple workouts at once using a CSV file."
          destination={selectedBulkUploadTrack?.name || 'Select destination'}
          csvContent={csvImport.csvContent}
          fileName={csvImport.fileName}
          result={csvImport.result}
          isUploading={csvImport.isUploading}
          onFileSelect={csvImport.onFileSelect}
          onUpload={handleBulkUpload}
          onClose={() => {
            setShowBulkUploadModal(false);
            setBulkUploadTrackId('');
            csvImport.reset();
          }}
          uploadDisabled={!bulkUploadTrackId}
          onDownloadTemplate={downloadCsvTemplate}
          selector={
            <div className="space-y-2">
              <Label htmlFor="daily-wod-upload-destination">Destination programme *</Label>
              <select
                id="daily-wod-upload-destination"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
                value={bulkUploadTrackId}
                onChange={(event) => setBulkUploadTrackId(event.target.value)}
              >
                <option value="">Select a programme...</option>
                {tracks
                  .filter((track) => track.is_active)
                  .map((track) => (
                    <option key={track.id} value={track.id}>
                      {track.name} ({track.workout_count} workouts)
                    </option>
                  ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Rows without a track value in the CSV will be uploaded to this destination.
              </p>
            </div>
          }
          instructions={
            <>
              <p className="font-medium text-sm text-foreground mb-1">Column format:</p>
              <p>
                <strong>Categories</strong>, <strong>date</strong> (YYYY-MM-DD, required),{' '}
                <strong>title</strong> (required), <strong>description</strong>
              </p>
              <p>
                <strong>duration</strong> (number only, e.g. 30 — not "30m"),{' '}
                <strong>workout</strong> (pipe-separated: A|B|C), <strong>equipment</strong>{' '}
                (pipe-separated)
              </p>
              <p>
                <strong>Coach notes</strong>, <strong>video_url</strong>, <strong>metrics</strong>{' '}
                (required: time / reps / rounds_reps / distance / calories / shots)
              </p>
              <p className="text-amber-400 mt-1">
                ⚠ Row 1 in the template is an example — delete it before uploading your data.
              </p>
            </>
          }
        />
      )}

      {/* AI Chat Sidebar */}
      <ChatSidebar
        isOpen={showAiChat}
        onClose={() => setShowAiChat(false)}
        onWorkoutGenerated={handleAiWorkoutGenerated}
      />
    </DashboardLayout>
  );
}

function showProgrammingFieldErrors(
  details: FieldErrors | undefined,
  setErrors: (errors: FieldErrors) => void,
  idPrefix = ''
) {
  const field = firstInvalidField(details);
  const ids: Record<string, string> = {
    name: `${idPrefix}workout-name`,
    scheduled_date: `${idPrefix}scheduled-date`,
    description: `${idPrefix}workout-description`,
  };
  const id = field ? ids[field] : undefined;
  if (!details || !id) return false;
  setErrors(details);
  requestAnimationFrame(() => document.getElementById(id)?.focus());
  return true;
}
